/**
 * The two pieces of save state that belong to the browser tab rather than to
 * one component: the latch that makes one gesture one Save, and whether this
 * tab has given up on asking the backend for a version before a Save.
 *
 * Both are module scope on purpose. The saving atom is the render state — it
 * is what disables the Save button and the drawers' controls — but a Recoil
 * write is only visible to a later read once React has committed it, so two
 * triggers in one tick both read `false` and both save. The latch below is
 * read and taken in the same statement, so the second trigger loses. And the
 * header, both layout drawers and the template swap are separate components
 * with separate hook instances: a ref inside a hook would give each of them
 * its own latch, which is no latch at all.
 */

/** The gesture holding the latch, or null while no Save is writing the line. */
let holder: symbol | null = null

/** The token to release with, or null when another Save holds the latch. */
export const takeEditorSaveLatch = (): symbol | null => {
  if (holder !== null) return null
  holder = Symbol('editor-save')
  return holder
}

/**
 * Frees the latch for the gesture that took it, and says whether it did. A
 * gesture whose token is no longer the holder's has been abandoned — the
 * editor re-initialised under it — and its release is a no-op, because the
 * latch it would free belongs to whichever Save started since; the answer
 * `false` tells that gesture the saving state is no longer its to clear
 * either.
 */
export const releaseEditorSaveLatch = (token: symbol | null): boolean => {
  if (token === null || token !== holder) return false
  holder = null
  return true
}

/**
 * Frees the latch whoever holds it, for an editor that is being re-initialised
 * out from under a Save: the abandoned gesture's own release then does
 * nothing, so it cannot free a latch the next Save has taken.
 */
export const dropEditorSaveLatch = (): void => {
  holder = null
}

let preSaveVersionDisabled = false

/**
 * A pre-save call that timed out or took a long time to fail costs every
 * later Save that same wait before it can write. One such answer is enough to
 * stop asking for the rest of the tab's life; the Save still records a
 * version per page write.
 */
export const disablePreSaveVersionForSession = (): void => {
  preSaveVersionDisabled = true
}

export const preSaveVersionDisabledForSession = (): boolean =>
  preSaveVersionDisabled

/** Test-only: the module's state outlives a component tree, tests must not. */
export const resetEditorSaveSession = (): void => {
  holder = null
  preSaveVersionDisabled = false
}
